import React, { useRef, useState, useCallback, useEffect, memo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { isMobile } from "react-device-detect";
import { debounce } from "lodash";
import CustomPagination from "../../CustomPagination";
import SeeMoreTile from "../../../../Screens/DashboardView/StayUpToDate/StayUpToDateComponents/StructureTemplates/SeeMoreTile";
import {
  addHttpToLink,
  handleNavigate,
} from "../../../../Utils/StayUpToDate/RenderComponent";
import { BsHourglassSplit, BsPinAngleFill } from "react-icons/bs";
import Card from "react-bootstrap/esm/Card";
import EllipsisTooltipText from "../../../ProductName/EllipsisTooltipText";
import { getComponentViewTransitionName } from "../../../../Utils/viewTransitions";
import { convertToWebP, preserveBrokenImageBox } from "../../../../Utils/imageUtils";
import { normalizeSoundValue, visibleCarouselDescription } from "../../../../Utils/helpers/carouselHelper";
import { CarouselHeading } from "../../CarouselHeading";
import {isLikelyVideoUrl} from "../../../../Utils/media/feedMedia";
import CarouselPostMedia from "../../../CarouselStructure/CarouselPostMedia";
import CarouselMediaSlot from "../../../CarouselStructure/CarouselMediaSlot";

const renderPinnedBadge = (isPinned:boolean) =>
    isPinned ? (
      <div className="pinned-badge" aria-label="Pinned post" style={{right:"4px"}}>
        <BsPinAngleFill />
      </div>
    ) : null;

const PostTemplate = (props: any) => {
  const {
    processedData,
    total_count,
    component,
    user_id,
    username,
    handleRedirect,
    brand_color,
    title,
    from,
    type,
    itemsPerPage = 3,
    platform,
    designTokens,
    pinnedPostIds,
    containerRef: externalContainerRef,
    currentIndex: externalCurrentIndex,
    setCurrentIndex: externalSetCurrentIndex,
    isButtonClicked: externalIsButtonClicked,
    isLinks,
  } = props;

  const param = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Use external refs/states if provided, otherwise create internal ones
  const internalContainerRef = useRef<any>(null);
  const internalIsButtonClicked = useRef(false);
  const [internalCurrentIndex, setInternalCurrentIndex] = useState(0);

  const containerRef = externalContainerRef || internalContainerRef;
  const isButtonClicked = externalIsButtonClicked || internalIsButtonClicked;
  const currentIndex =
    externalCurrentIndex !== undefined
      ? externalCurrentIndex
      : internalCurrentIndex;
  const setCurrentIndex = externalSetCurrentIndex || setInternalCurrentIndex;

  const [isSoundOn, setIsSoundOn] = useState(() =>
    normalizeSoundValue(component?.content_sound, true)
  );
  const [isRowScrolling, setIsRowScrolling] = useState(false);
  const isScrollingProgrammatically = useRef(false);
  const rowScrollEndTimer = useRef<number | null>(null);
  // Landing horizontal rows: IntersectionObserver mounts HoverVideoPlayer only
  // while cards are near the scroll viewport (unmounts when away) to limit
  // concurrent <video> elements on Safari iOS. preload="none" still defers
  // network until hover. Detail grids keep all media interactive.
  const isLandingCarousel = !param?.id;

  useEffect(() => {
    setIsSoundOn(normalizeSoundValue(component?.content_sound, true));
  }, [component?.content_sound]);

  // Scroll handling logic - common to both ReelsData and TiktokData
  const handleScroll = useCallback(
    debounce(() => {
      // Don't update index if button was clicked or we're programmatically scrolling
      if (
        !isButtonClicked.current &&
        !isScrollingProgrammatically.current &&
        containerRef.current &&
        processedData &&
        processedData.length > 0
      ) {
        const scrollLeft = containerRef.current.scrollLeft;
        const scrollWidth = containerRef.current.scrollWidth;
        const clientWidth = containerRef.current.clientWidth;
        const totalItems = processedData.length;

        // Calculate scrollable width (total width - visible width)
        const scrollableWidth = scrollWidth - clientWidth;

        if (scrollableWidth > 0 && totalItems > 1) {
          // Calculate item width based on scrollable width
          const itemWidth = scrollableWidth / (totalItems - 1);
          const newCurrentIndex = Math.round(scrollLeft / itemWidth);

          // Only update if index actually changed to prevent unnecessary re-renders
          if (
            newCurrentIndex !== currentIndex &&
            newCurrentIndex >= 0 &&
            newCurrentIndex < totalItems
          ) {
            setCurrentIndex(newCurrentIndex);
          }
        }
      }
    }, 100), // 100ms debounce for stability
    [
      processedData,
      currentIndex,
      containerRef,
      setCurrentIndex,
      isButtonClicked,
    ]
  );

  // Scroll to position when currentIndex changes (only when button is clicked).
  // Do not depend on content_sound — mute is handled by the effect above;
  // including it here re-ran scroll logic and jumped the strip to the start.
  useEffect(() => {
    if (
      isButtonClicked.current &&
      containerRef.current &&
      processedData &&
      processedData.length > 0
    ) {
      const scrollWidth = containerRef.current.scrollWidth;
      const clientWidth = containerRef.current.clientWidth;
      const totalItems = processedData.length;

      // Calculate scrollable width
      const scrollableWidth = scrollWidth - clientWidth;

      if (scrollableWidth > 0 && totalItems > 1) {
        // Calculate scroll position based on scrollable width
        const itemWidth = scrollableWidth / (totalItems - 1);
        const scrollPosition = itemWidth * currentIndex;

        // Set flag to prevent scroll handler from interfering
        isScrollingProgrammatically.current = true;

        // Use requestAnimationFrame for smoother scrolling
        requestAnimationFrame(() => {
          if (containerRef.current) {
            containerRef.current.scrollTo({
              left: scrollPosition,
              behavior: "smooth",
            });

            // Reset flag after scroll animation completes
            setTimeout(() => {
              isScrollingProgrammatically.current = false;
            }, 500);
          }
        });
      }
    }
  }, [
    currentIndex,
    processedData,
    containerRef,
    isButtonClicked,
  ]);

  // Keep latest handleScroll without rebinding scroll UI handlers.
  const handleScrollRef = useRef(handleScroll);
  handleScrollRef.current = handleScroll;

  const markRowScrolling = useCallback(() => {
    if (!isLandingCarousel) return;

    setIsRowScrolling(true);
    if (rowScrollEndTimer.current !== null) {
      window.clearTimeout(rowScrollEndTimer.current);
    }
    rowScrollEndTimer.current = window.setTimeout(() => {
      rowScrollEndTimer.current = null;
      setIsRowScrolling(false);
    }, 200);
  }, [isLandingCarousel]);

  // onScroll alone fires after movement has started. Also catch wheel /
  // touchmove so isRowScrolling flips true at gesture start.
  const onContainerScroll = useCallback(() => {
    handleScrollRef.current();
    markRowScrolling();
  }, [markRowScrolling]);

  const onContainerWheel = useCallback(() => {
    markRowScrolling();
  }, [markRowScrolling]);

  const onContainerTouchMove = useCallback(() => {
    markRowScrolling();
  }, [markRowScrolling]);

  useEffect(() => {
    return () => {
      if (rowScrollEndTimer.current !== null) {
        window.clearTimeout(rowScrollEndTimer.current);
        rowScrollEndTimer.current = null;
      }
    };
  }, []);

  const handlePrevPage = () => {
    if (isButtonClicked.current) return; // Prevent rapid clicks

    isButtonClicked.current = true;
    const newIndex = Math.max(currentIndex - 1, 0);
    setCurrentIndex(newIndex);

    // Reset button clicked flag after scroll animation completes
    setTimeout(() => {
      isButtonClicked.current = false;
    }, 600); // Timeout to ensure smooth scroll completes
  };

  const handleNextPage = () => {
    if (isButtonClicked.current) return; // Prevent rapid clicks

    isButtonClicked.current = true;
    const maxIndex = Math.max((processedData?.length || 0) - itemsPerPage, 0);
    const newIndex = Math.min(currentIndex + 1, maxIndex);
    setCurrentIndex(newIndex);

    // Reset button clicked flag after scroll animation completes
    setTimeout(() => {
      isButtonClicked.current = false;
    }, 600); // Timeout to ensure smooth scroll completes
  };

  const PodcastImagePostTag = memo((props: any) => {
    const { data, from, designTokens, pinnedPostIds } = props;
    const { thumbnail, media_url, permalink, caption, post_id } = data;
    const isDefaultTemplate = designTokens?.templateName === "Default";

    const getWrapperClassName = useCallback(() => {
      const baseClass = "creator-feed-image-wrapper-pod";
      const classes = [baseClass];
      if(!isDefaultTemplate)classes.push("podcast-item-wrapper")

      if (component?.content_shape == "sq") classes.push("square-wrapper");

      return classes.join(" ");
    }, [component?.content_shape]);

    const getImageClassName = useCallback(() => {
      const baseClass = "creator-feed-image-wrapper-podcast";
      const classes = [baseClass];

      if (component?.content_shape == "sq") classes.push("square-image");

      return classes.join(" ");
    }, [component?.content_shape]);

    const getWrapperStyle = useCallback(() => {
      if (designTokens && isDefaultTemplate) {
        return {
          backgroundColor: isDefaultTemplate
            ? "#f1f1f1"
            : designTokens?.forthColor || "#f1f1f1",
          border: designTokens?.tertiaryColor
            ? `1px solid ${designTokens?.tertiaryColor}`
            : undefined,
        };
      }

      return {
        // backgroundColor: brand_color || undefined,
        ...(component?.content_shape !== "sq"
          ? { height: "var(--content-rectangle-height)" }
          : {}),
      };
    }, [designTokens, isDefaultTemplate, brand_color]);

    const getImageStyle = useCallback(() => {
      if (component?.content_shape == "sq") {
        return { width: "100% !important", height: "80px", borderRadius: "4px" };
      }

      return {
        // height: "55%",
        // backgroundColor: isDefaultTemplate ? "#f1f1f1" : designTokens?.forthColor || "#f1f1f1",
      };
    }, [component?.content_shape, designTokens, isDefaultTemplate]);

    const handleClick = useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault();
        if (permalink && permalink !== "") {
          const imageUrl = isLikelyVideoUrl(media_url) ? thumbnail : media_url;
          handleRedirect(
            post_id,
            convertToWebP(imageUrl),
            "Podcast Content",
            caption,
            platform,
            "",
            from
          );
          window.open(addHttpToLink(permalink), "_blank");
        }
      },
      [post_id, media_url, thumbnail, caption, permalink]
    );

    return (
      <a
        href={addHttpToLink(permalink)}
        target="_blank"
        rel="noopener noreferrer"
        className="podcast-link-tag"
        onClick={handleClick}
      >
        <div className="creator-feed-card-container">
          {!media_url ? (
            <div className="creator-feed-card-podcast not-available">
              <BsHourglassSplit className="hourglassSplit" />
              <p>
                Work in progress: This content will be available in a few
                minutes.
              </p>
            </div>
          ) : (
            <div className={getWrapperClassName()} style={getWrapperStyle()}>
              {pinnedPostIds?.length > 0 && renderPinnedBadge(pinnedPostIds?.includes(data?.post_id))}
              <div
                className={getImageClassName()}
                style={{
                  ...getImageStyle(),
                 
                }}
              >
                <Card.Img
                  variant="top"
                  src={convertToWebP(isLikelyVideoUrl(media_url) ? thumbnail : media_url)}
                  style={{
                    ...getImageStyle(),
                    ...(component?.content_shape == "sq"
                      ? { height: "80px", borderRadius: "4px" }
                      : { height: "55%" })
                  }
                  }
                  className={`creator-feed-card-podcast-image ${component?.content_shape == "sq" ? "podcast-image" : ""
                    }`}
                  loading="eager"
                />
                <EllipsisTooltipText
                  className={`caption ${component?.content_shape == "sq" ? "podcast-text-square" : "podcast-text-rectangle"
                    }`}
                  text={caption}
                  style={{fontFamily: designTokens?.secondaryFontFamily}}
                />
              </div>
            </div>
          )}
        </div>
      </a>
    );
  });

  return (
    <div className="reels-wrapper">
      <div className="mainDiv">
        <CarouselHeading
          name={component?.name}
          description={visibleCarouselDescription(component, {
            from,
            isIndividualPage: false,
          })}
          nameStyle={{
            ...(param?.id ? { cursor: "auto" } : {}),
            viewTransitionName: getComponentViewTransitionName(component),
            fontFamily: designTokens?.primaryFontFamily
          }}
          descriptionStyle={{
            fontFamily: designTokens?.secondaryFontFamily,
          }}
          onNameClick={() =>
            handleNavigate(
              navigate,
              location,
              username,
              user_id,
              component,
              from
            )
          }
        />
        {/* {!param?.id && (
          <CustomPagination
            totalItems={(processedData?.length)  || 0}
            currentIndex={currentIndex}
            handlePrevPage={handlePrevPage}
            handleNextPage={handleNextPage}
          />
        )} */}
      </div>
      <div
        className={!param?.id ? "outer-container-reels" : ""}
        ref={containerRef}
        onScroll={onContainerScroll}
        onWheel={onContainerWheel}
        onTouchMove={onContainerTouchMove}
      >
        <div
          className={
            !param?.id
              ? "links-grid-container-reels"
              : isMobile
                ? "item-grid creator-card-grid-wrp"
                : "item-grid"
          }
        >
          {processedData &&
            processedData.slice(0, 25).map((link: any, index: number) => (
              <CarouselMediaSlot
                key={link.post_id || link.id || index}
                scrollRootRef={containerRef}
                observeViewport={isLandingCarousel}
                className={
                  String(link?.platform || "").toLowerCase() === "youtube" &&
                  !link?.product_image
                    ? "link-url youtube-link-position"
                    : "link-url"
                }
              >
                {(enableInteractiveMedia) =>
                  link?.platform == "podcast" ? (
                    <PodcastImagePostTag
                      data={link}
                      from={from}
                      designTokens={designTokens}
                      pinnedPostIds={pinnedPostIds}
                    />
                  ) : (
                    <CarouselPostMedia
                      link={link}
                      index={index}
                      handleShop={handleRedirect}
                      title={title}
                      brand_color={brand_color}
                      isLinks={
                        typeof isLinks === "boolean"
                          ? isLinks
                          : Boolean(link?.shop_link)
                      }
                      isDashboard={false}
                      contentShape={component?.content_shape}
                      contentSound={component?.content_sound}
                      isSoundOn={isSoundOn}
                      type={type}
                      componentType={type}
                      from={from}
                      pinnedPostIds={pinnedPostIds}
                      designTokens={designTokens}
                      enableInteractiveMedia={enableInteractiveMedia}
                      disableHoverPlayback={isRowScrolling}
                    />
                  )
                }
              </CarouselMediaSlot>
            ))}
          {/* Only show "See more" once we’ve actually loaded/rendered the first page worth of items.
              Otherwise carousels with itemsPerPage=3 can show "Load more" prematurely. */}
          {!param?.id && total_count > 25 && Array.isArray(processedData) && processedData.length >= 25 && (
            <SeeMoreTile
              data={processedData}
              total_count={total_count}
              type={type}
              unique_id={component?.route_name || component?.unique_id}
              user_id={user_id}
              username={username}
              component={component}
              from={from}
              designTokens={designTokens}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default PostTemplate;
